import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getLanguagePref, updateLanguagePref } from "../api/userRequests";
import { DEFAULT_LANGUAGE, getLanguageDirection, isSupportedLanguage } from "./supportedLanguages";
import en from "./locales/en.json";
import es from "./locales/es.json";
import zhHans from "./locales/zh-Hans.json";
import id from "./locales/id.json";

const dictionaries = {
  en,
  es,
  "zh-Hans": zhHans,
  id
};

const STORAGE_KEY = "iiss_language_pref";

const TranslationContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key
});

export const TranslationProvider = ({ children, user }) => {
  // Read localStorage synchronously on first render so the UI doesn't flash
  // back to English while the network request below is in flight.
  const [language, setLanguageState] = useState(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved && isSupportedLanguage(saved) ? saved : DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    const loadPreference = async () => {
      if (!user) return;
      try {
        const response = await getLanguagePref(user.userId);
        const savedLanguage = response.data?.language_preference;
        if (savedLanguage && isSupportedLanguage(savedLanguage)) {
          setLanguageState(savedLanguage);
          window.localStorage.setItem(STORAGE_KEY, savedLanguage);
        }
      } catch (err) {
        console.error("Error loading language preference:", err);
      }
    };
    loadPreference();
  }, [user]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = getLanguageDirection(language);
  }, [language]);

  const setLanguage = async (newLanguage) => {
    if (!isSupportedLanguage(newLanguage)) return;
    setLanguageState(newLanguage);
    window.localStorage.setItem(STORAGE_KEY, newLanguage);
    if (user) {
      try {
        await updateLanguagePref({
          user_id: user.userId,
          language_pref: newLanguage
        });
      } catch (err) {
        console.error("Error saving language preference:", err);
      }
    }
  };

  const value = useMemo(() => {
    const dictionary = dictionaries[language] || dictionaries[DEFAULT_LANGUAGE];
    const fallback = dictionaries[DEFAULT_LANGUAGE];
    return {
      language,
      setLanguage,
      t: (key, replacements = {}) => {
        let text = dictionary[key] || fallback[key] || key;
        Object.entries(replacements).forEach(([name, value]) => {
          text = text.replaceAll(`{{${name}}}`, value);
        });
        return text;
      }
    };
  }, [language]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);